import { has, isNil } from "lodash";
import moment from "moment";
import { UNDETERMINED_FUTURE } from "PFApp/constants/dates";
import { useGrowl } from "PFApp/use_growl";
import { useTemplate } from "PFCore/helpers/templates";
import { useResponseErrors } from "PFCore/helpers/use_response_errors";
import { useBookingTemplateUpdate, useBookingUpdate } from "PFCore/hooks/queries";
import { useBookingTemplateCreate } from "PFCore/hooks/queries/bookings/booking_templates/use_booking_template_create";
import { useBookingsReassign } from "PFCore/hooks/queries/bookings/reassign/use_bookings_reassign";
import { useBookingCreate } from "PFCore/hooks/queries/bookings/use_booking_create";
import { useDateFormatter } from "PFCore/hooks/use_date_formatter";
import { Engagement, Profile } from "PFTypes";
import { RefObject, useState } from "react";
import { useTranslation } from "react-i18next";

import { useRefreshBookingData } from "../../hooks/use_refresh_booking_data";
import { useSyncRows } from "../../parts/overview/use_sync_rows";
import { useBookingActivityContext } from "../../parts/providers/booking_activity_context_provider";
import { bookRoleByShortlist } from "../../services/api";
import { BookingSelectOption } from "../booking_questions/booking_questions";
import { ReassignSelectOption } from "../reassign_booking_modal/bookings_to_reassign_selector";
import {
  getSubmitErrorMessageTranslationKey,
  getSubmitSuccessMessageTranslationKey
} from "./booking_modal.utils";
import { mapRequirementToBookingData } from "./requirement_selector";
import { BookingModalDataType, BookingModalMode, IBookingModalItem } from "./use_booking_modal";
import { checkIsGroupBooking } from "./use_engagement_select.utils";

const getActivityId = (
  initialActivityId: number | null | undefined,
  selectedEngagementId: number | undefined
): number | undefined => {
  if (initialActivityId && selectedEngagementId && initialActivityId !== selectedEngagementId) {
    return selectedEngagementId;
  }
  return initialActivityId ?? selectedEngagementId;
};

interface UseHandleSubmit {
  bookingData: Record<string, IBookingModalItem>;
  initialData: BookingModalDataType;
  title?: string;
  description?: string;
  prefilledUserRef: RefObject<Profile | null>;
  selectedUser?: { id?: number; search_id?: number; profiles?: { id: number }[] };
  selectedBookingCategory?: { id: number };
  mode: BookingModalMode;
  bookingId?: number;
  specificTime?: boolean;
  overridesCalendar?: boolean;
  overridesNonWorkingDays?: boolean;
  onSubmit: (data: any) => void | Promise<any>;
  onSuccess: () => void;
  onError: () => void;
  updateGroupOptionSelected: BookingSelectOption;
  updateRepeatOptionSelected: BookingSelectOption;
  reassignOptionSelected: ReassignSelectOption;
  roleBookingsIds: number[];
  selectedEngagement: Engagement | null;
}

const useHandleSubmit = ({
  bookingData,
  initialData,
  title,
  description,
  selectedUser,
  prefilledUserRef,
  selectedBookingCategory,
  mode,
  bookingId,
  overridesCalendar,
  overridesNonWorkingDays,
  onSubmit,
  onSuccess,
  onError,
  specificTime,
  updateGroupOptionSelected,
  updateRepeatOptionSelected,
  reassignOptionSelected,
  roleBookingsIds,
  selectedEngagement
}: UseHandleSubmit) => {
  const growl = useGrowl();
  const { t } = useTranslation("bookingModule");

  const { formatISODate } = useDateFormatter();
  const syncRows = useSyncRows();

  const { activity } = useBookingActivityContext();
  const activityTemplate = useTemplate(activity?.template_id);
  const isActivityEngagement = activityTemplate?.key === "engagement";

  const profileIds: number[] = selectedUser?.id
    ? [selectedUser?.id]
    : (selectedUser?.profiles || []).map(({ id }) => id);
  const updateAllGroup = updateGroupOptionSelected === BookingSelectOption.All;

  const { mutate: bulkReassignBookings } = useBookingsReassign({
    oldProfileId: prefilledUserRef.current?.id ?? undefined,
    newProfileId: selectedUser?.id ?? undefined,
    roleBookingsIds,
    activityId: initialData?.activityId ?? undefined
  });

  const { update: updateBooking } = useBookingUpdate(updateAllGroup ? undefined : bookingId);
  const { update: updateBookingTemplate } = useBookingTemplateUpdate(
    updateAllGroup ? undefined : initialData.bookingTemplate?.id
  );
  const { create: createBooking } = useBookingCreate(...profileIds);
  const { create: createBookingTemplate } = useBookingTemplateCreate(...profileIds);

  const refreshBookingData = useRefreshBookingData();

  const [errors, setErrors] = useState<Record<string, string> | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const { getFormattedErrors } = useResponseErrors();

  const getRequestPayload = () => {
    const getBookingPayload = (index) => {
      const { requirement } = bookingData[index];
      const payload: { [k: string]: any } = {
        start_date: bookingData[index].start, //eslint-disable-line camelcase
        end_date: bookingData[index].end, //eslint-disable-line camelcase
        start_time: bookingData[index].startTime, //eslint-disable-line camelcase
        end_time: bookingData[index].endTime, //eslint-disable-line camelcase
        ...mapRequirementToBookingData({ type: requirement.type, value: requirement.value! }),
        booking_category_id: selectedBookingCategory?.id, //eslint-disable-line camelcase
        wday_mask: bookingData[index].wdayMask, //eslint-disable-line camelcase
        title,
        description,
        overrides_calendar: overridesCalendar, // eslint-disable-line camelcase
        overrides_non_working_days: overridesNonWorkingDays, // eslint-disable-line camelcase
        phase_source_id: bookingData[index].phaseSourceId //eslint-disable-line camelcase
      };
      const isNotABulkSelect = !checkIsGroupBooking(selectedUser);
      if ((isActivityEngagement || selectedEngagement?.id) && isNotABulkSelect) {
        payload.activity_id = selectedEngagement?.id; //eslint-disable-line camelcase
      }
      if (!updateAllGroup && initialData.activityId) {
        payload.profile_id = selectedUser?.id; //eslint-disable-line camelcase
      }
      if ((mode === "edit" || mode === "clone") && updateAllGroup) {
        payload.booking_group_id = initialData.bookingGroupId; //eslint-disable-line camelcase
      } else if (selectedUser?.search_id) {
        payload.search_id = selectedUser?.search_id; //eslint-disable-line camelcase
      } else if (specificTime && selectedUser?.id) {
        payload.profile_id = selectedUser.id; //eslint-disable-line camelcase
      }
      if (mode === "edit" && specificTime && updateRepeatOptionSelected === BookingSelectOption.Future) {
        //eslint-disable-next-line camelcase
        payload.date_range = {
          min: formatISODate(initialData.bookings?.[index]?.startDate),
          max: formatISODate(UNDETERMINED_FUTURE)
        };
      }
      return payload;
    };
    const bookingDataKeys = Object.keys(bookingData);

    if (mode === "edit") {
      return [getBookingPayload(0)];
    }
    if (specificTime) {
      return bookingDataKeys.map(getBookingPayload);
    }
    return [
      {
        profile_id: updateAllGroup ? undefined : selectedUser?.id, // eslint-disable-line camelcase
        // replace it with
        // !isBulkSelectOption(selectedUser)
        // after merge back
        activity_id: !has(selectedUser, "search_id")
          ? getActivityId(initialData?.activityId, selectedEngagement?.id)
          : undefined, //eslint-disable-line camelcase
        source_type: "ui", // eslint-disable-line camelcase
        search_id: selectedUser?.search_id, // eslint-disable-line camelcase
        bookings: bookingDataKeys.map(getBookingPayload)
      }
    ];
  };

  const handleSubmit = async () => {
    if (!bookingData) {
      return;
    }

    setErrors(null);
    setIsLoading(true);
    const payload = getRequestPayload();

    try {
      const apiFunctions = {
        booking: { create: createBooking, update: updateBooking },
        bookingTemplate: { create: createBookingTemplate, update: updateBookingTemplate }
      };
      const apiCallFunction =
        apiFunctions[specificTime ? "bookingTemplate" : "booking"][mode === "edit" ? "update" : "create"];

      if (!apiCallFunction) {
        return;
      }
      const bookings = await Promise.all<Promise<{ id: number }>[]>(
        payload.map((item) => apiCallFunction(item))
      );
      if (initialData.shortlistId) {
        await bookRoleByShortlist(initialData.shortlistId);
      }
      if (
        reassignOptionSelected === ReassignSelectOption.AllBookings &&
        initialData.activityId &&
        prefilledUserRef.current?.id &&
        selectedUser?.id
      ) {
        const min = formatISODate(moment().subtract(2, "year"));
        const max = formatISODate(moment().add(2, "year"));

        await bulkReassignBookings({
          activityId: initialData.activityId,
          oldProfileId: prefilledUserRef.current?.id,
          newProfileId: selectedUser?.id,
          dateRange: {
            min,
            max
          }
        });
      }
      refreshBookingData({
        bookingIds: bookings.map(({ id }) => id),
        bookingGroupId: initialData.bookingGroupId,
        shortlistId: initialData.shortlistId,
        activityId: getActivityId(initialData?.activityId, selectedEngagement?.id),
        profileId: selectedUser?.id,
        refreshGroup: updateAllGroup || (mode === "create" && !isNil(selectedUser?.search_id)),
        refreshProfile: specificTime
      });
      syncRows({
        profileId: selectedUser?.id,
        activityId: initialData.activityId ?? selectedEngagement?.id,
        parentActivityIds: [initialData.parentActivityId]
      });
      await onSubmit?.({
        profileId: selectedUser?.id,
        activityId: getActivityId(initialData?.activityId, selectedEngagement?.id),
        parentActivityIds: [initialData.parentActivityId]
      });
      growl({
        kind: "success",
        message: t(getSubmitSuccessMessageTranslationKey(mode))
      });
      setIsLoading(false);
      onSuccess?.();
    } catch (response) {
      const responseErrors = getFormattedErrors(response);
      setIsLoading(false);
      if (Object.keys(responseErrors).length > 0) {
        setErrors(responseErrors);
        return;
      }
      growl({
        kind: "error",
        message: t(getSubmitErrorMessageTranslationKey(mode))
      });
      onError?.();
    }
  };

  return {
    handleSubmit,
    isLoading,
    errors
  };
};

export default useHandleSubmit;
